import { useEffect, useRef, useLayoutEffect, Suspense } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { motion } from "framer-motion";
import Draggable from "react-draggable";
import { IconButton, Button, BUTTON_ROLES, Pdf } from "@/comn/components";
import { modalState } from "@/comn/features/recoil";
import { comnEnvs } from "@/comn/utils";
import ComnCodeModule from "@/comn/comn/code";
import { CommonErrors } from "@/comn/components";
import { AprvRqstDrt } from "@/comn/comn/aprv/AprvRqstDrt";
import { RoleAprvRqstDrt } from "@/comn/comn/aprv/RoleAprvRqstDrt";
import Cookies from "js-cookie";
import { _TEST1234 } from "@/comn/features/config/ApiConfig";
import { v4 as uuid } from "uuid";

const MODAL_SIZES = {
    xs: "w-[30vw] max-w-[30vw] sm:w-full sm:max-w-[90vw] md:w-full md:max-w-[50vw]",
    sm: "w-[50vw] max-w-[50vw] max-h-[90vh] sm:w-full sm:max-w-[90vw] md:w-full md:max-w-[60vw]",
    md: "w-[70vw] max-w-[70vw] max-h-[90vh] sm:w-full sm:max-w-[90vw] md:w-full md:max-w-[70vw]",
    lg: "w-[80vw] max-w-[80vw] max-h-[90vh] sm:w-full sm:max-w-[90vw] md:w-full md:max-w-[80vw]",
    xl: "w-[90vw] max-w-[90vw] max-h-[90vh] sm:w-full sm:max-w-[90vw] md:w-full md:max-w-[90vw]",
};

export type ModalProps = {
    id?: string;
    content?: React.ReactNode;
    type?: "deleted" | "pdf" | "error" | "image" | "messages";
    backdrop?: boolean;
    draggable?: boolean;
    close?: boolean;
    topClose?: boolean;
    confirm?: keyof typeof BUTTON_ROLES;
    title?: string;
    url?: string;
    params?: any;
    size?: keyof typeof MODAL_SIZES;
    height?: string | number;
    layout?: "popup" | "main";

    // pdf
    data?: any;
    defaultPage?: number;
    thumbnail?: boolean;
    print?: boolean;
    download?: boolean;

    confirmText?: string;
    cancelText?: string;

    callback?: (arg?: any) => void;
    onConfirm?: (arg?: any) => void;
    onCancel?: (arg?: any) => void;
    onClose?: (arg?: any) => void;
};

const Backdrop = () => {
    return (
        <motion.div
            className="fixed w-screen h-screen z-[9997] top-0 left-0"
            initial={{ opacity: 0 }}
            animate={{ background: "#000", opacity: 0.4 }}
            transition={{ duration: 0.1 }}
        />
    );
};

const Modal = (props: ModalProps) => {
    const {
        // iframe
        url,
        params, // &code
        layout = "popup",

        id,
        title,
        type,
        content,
        height,
        confirmText,
        cancelText,
        draggable = false,
        backdrop = true,
        close = true,
        topClose = true,
        confirm,
        size = url || type === "pdf" || type === "messages" ? "xl" : "xs",

        // callback
        callback,
        onConfirm,
        onCancel,
        onClose,

        // pdf
        data,
        defaultPage = 1,
        thumbnail,
        print,
        download,
    } = props;

    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const authlinkKey = useRef(uuid());
    const setModal = useSetRecoilState(modalState);

    useEffect(() => {
        if (ref.current) {
            ref.current.focus();
        }
    }, []);

    useEffect(() => {
        return () => {
            const domain =
                window.location.hostname.split(".").length > 1
                    ? window.location.hostname.replace(window.location.hostname.split(".")[0], "")
                    : window.location.hostname;
            const AUTHLINK_KEY = authlinkKey.current;
            Cookies.remove(`${AUTHLINK_KEY}_a`, { domain });
            Cookies.remove(`${AUTHLINK_KEY}_r`, { domain });
        };
    }, []);

    useLayoutEffect(() => {
        if (!ref.current) return;
        const htmlCollection = ref.current.getElementsByClassName("uf-page-navigation");
        Array.from(htmlCollection).forEach((element) => {
            //@ts-ignore
            element.style.display = "none";
        });
    }, []);

    useEffect(() => {
        if (url) {
            window.addEventListener("message", handleMessage);
        }

        return () => {
            if (url) {
                window.removeEventListener("message", handleMessage);
            }
        };
    }, []);

    const handleClose = () => {
        if (onClose instanceof Function) onClose();
        setModal((prev) => prev.filter((v) => id !== v.id));
    };

    const handleCancel = () => {
        if (onCancel instanceof Function) onCancel();
        handleClose();
    };

    const handleConfirm = () => {
        if (onConfirm instanceof Function) onConfirm();
        handleClose();
    };

    const handleMessage = (event: any) => {
        const contentWindow =
            //@ts-ignore
            document.getElementsByName(id)?.[0]?.contentWindow ||
            //@ts-ignore
            document.getElementsByName(authlinkKey.current + "-link")?.[0]?.contentWindow;

        if (!contentWindow) return;
        if (event.source !== contentWindow) return;
        if (!callback) return;
        callback(event.data);
    };

    /**
     *
     *
     *
     *
     *
     */

    const _header =
        url || typeof content !== "string" ? (
            <div
                className={classNames("handle flex items-center justify-end p-1 py-0 pe-0", draggable && "cursor-move")}
            >
                {topClose && <IconButton icon="close" onClick={handleClose} />}
            </div>
        ) : (
            <div
                className={classNames(
                    "handle flex items-center justify-between p-1.5 py-1",
                    draggable && "cursor-move",
                )}
            >
                <div className="text-lg px-1 ps-0">{title ? t(title) : t("L_ALT")}</div>
                {topClose && <IconButton icon="close" onClick={handleClose} />}
            </div>
        );
    const _body = (() => {
        let _content;
        if (url) {
            if (Object.values(comnEnvs.popup).includes(url)) {
                _content = <ComnCodeModule id={id} COMN_CODE={url} callback={callback} params={params} />;
            } else {
                let IFRAME_URL;
                const isPopup = layout === "popup";
                const hasOriginQuery = url.split("/").pop()?.includes("?");
                const hasExtraQuery = Boolean(params) || isPopup;
                const QUERY_SIGN = hasExtraQuery ? "?" : "";
                const PARAMS_QUERY = params ? "&params=" + encodeURIComponent(JSON.stringify(params)) : "";
                const LAYOUT_QUERY = isPopup ? "&ppup=Y" : "";

                IFRAME_URL = (hasOriginQuery ? url : url + QUERY_SIGN) + PARAMS_QUERY + LAYOUT_QUERY;

                // 시스템 인증 연계
                const hasAuthlink = !IFRAME_URL.includes(window.location.hostname);
                const AUTHLINK_KEY = authlinkKey.current;
                if (hasAuthlink) {
                    const domain =
                        window.location.hostname.split(".").length > 1
                            ? window.location.hostname.replace(window.location.hostname.split(".")[0], "")
                            : window.location.hostname;
                    if (_TEST1234.AUTHORIZATION) {
                        document.cookie = `${AUTHLINK_KEY}_a=${_TEST1234.AUTHORIZATION}; domain=${domain}; path=/`;
                    }
                    if (_TEST1234.REFRESH_AUTHORIZATION) {
                        document.cookie = `${AUTHLINK_KEY}_r=${_TEST1234.REFRESH_AUTHORIZATION}; domain=${domain}; path=/`;
                    }
                }

                _content = (
                    <iframe
                        src={IFRAME_URL}
                        name={hasAuthlink ? authlinkKey.current + "-link" : id}
                        className="w-full min-h-[90vh] px-1"
                        title={"modal" + id}
                    />
                );
            }
        } else if (type === "messages") {
            _content = <CommonErrors errors={data} type="messages" />;
        } else if (type === "pdf") {
            _content = (
                <Pdf data={data} defaultPage={defaultPage} thumbnail={thumbnail} print={print} download={download} />
            );
        } else if (type === "deleted") {
            _content = t("msg.osc.00109");
        } else if (typeof content === "string") {
            _content = <pre className="whitespace-pre-wrap">{t(content)}</pre>;
        } else {
            _content = content;
        }
        return (
            <div
                className={classNames(
                    "w-full h-full overflow-auto",
                    typeof content === "string" || type === "deleted" ? "p-3 px-2 py-0" : "p-1 px-2 py-0",
                )}
            >
                {_content}
            </div>
        );
    })();
    const _footer = (close !== false || onConfirm !== undefined) && (
        <div className={"px-2 py-2 h-12 flex space-x-1 items-center justify-end"}>
            {(confirm || onConfirm) && (
                <Button onClick={() => handleConfirm()} role={confirm || "ok"}>
                    {confirmText}
                </Button>
            )}
            {close !== false && (
                <Button onClick={() => handleCancel()} role="close">
                    {cancelText}
                </Button>
            )}
        </div>
    );

    // url이 결재 일 경우 분기
    if (url === "/comn/comn/ppup/aprvRqstDrt") {
        return (
            <>
                <Backdrop />
                <AprvRqstDrt {...props} />
            </>
        );
    } else if (url === "/comn/comn/ppup/roleAprvRqstDrt") {
        return (
            <>
                <Backdrop />
                <RoleAprvRqstDrt {...props} />
            </>
        );
    }

    return (
        <>
            {backdrop && <Backdrop />}
            <Suspense fallback={<div></div>}>
                <Draggable
                    nodeRef={ref}
                    disabled={!draggable}
                    handle=".handle"
                    positionOffset={{ x: "-50%", y: "-50%" }}
                >
                    <motion.div
                        tabIndex={0}
                        ref={ref}
                        initial={{ opacity: 0.5 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.1 }}
                        className={classNames(
                            "fixed flex max-h-[95vh] flex-col top-1/2 left-1/2 h-auto border rounded bg-background z-[9997] focus:outline-none",
                            MODAL_SIZES[size],
                        )}
                        style={{ height }}
                    >
                        {_header}
                        {_body}
                        {_footer}
                    </motion.div>
                </Draggable>
            </Suspense>
        </>
    );
};

const CommonModal = () => {
    const ref = useRef(false);

    const [modal, setModal] = useRecoilState(modalState);

    const location = useLocation();

    useEffect(() => {
        if (ref.current === false) {
            ref.current = true;
            return;
        } else {
            //setModal([]);
            setModal((prev) => {
                const t: any[] = [];
                prev.map((item) => {
                    if (["anyAuth", "pwdAuth"].includes(item.id || "")) {
                        t.push(item);
                    }
                });
                return t;
            });
        }
    }, [location.pathname]);

    useEffect(() => {
        if (modal.length) document.body.style.overflowY = "hidden";
        else document.body.style.overflowY = "auto";
    }, [modal]);

    return createPortal(
        <div id="modal-content">
            {modal.map((props) => {
                return <Modal key={props.id} {...props} />;
            })}
        </div>,
        document.body,
    );
};

export default CommonModal;
